import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, UserPlus, Download, Loader2, CreditCard, Edit2, Trash2, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem("admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ROLE_BADGE = {
  super_admin: "bg-amber-50 text-amber-700 border-amber-200",
  admin:       "bg-indigo-50 text-indigo-700 border-indigo-200",
  contractor:  "bg-green-50 text-green-700 border-green-200",
};
const ROLE_LABEL = { super_admin: "Founder", admin: "Head of Ops", contractor: "Talent Partner" };

export function TeamTab() {
  const [members, setMembers] = useState([]);
  const [options, setOptions] = useState({ specialists: [], commission_tiers: {} });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // member being edited
  const currentRole = JSON.parse(localStorage.getItem("admin_info") || "{}")?.role;
  const isSuperAdmin = currentRole === "super_admin";

  const [form, setForm] = useState({
    name: "", email: "", username: "", password: "",
    role: "contractor", specialist: "", commission_tier: "25", expiry_date: "",
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [m, o] = await Promise.all([
        axios.get(`${API}/team/members`, { headers: authHeader() }),
        axios.get(`${API}/team/options`, { headers: authHeader() }),
      ]);
      setMembers(m.data.items || []);
      setOptions(o.data);
    } catch (e) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.username || !form.password) {
      toast.error("Name, email, username, password required"); return;
    }
    if (form.password.length < 8) { toast.error("Password must be 8+ chars"); return; }
    try {
      await axios.post(`${API}/team/members`, form, { headers: authHeader() });
      toast.success(`${form.role === "contractor" ? "Talent Partner" : "Admin"} created`);
      setShowCreate(false);
      setForm({ name: "", email: "", username: "", password: "", role: "contractor", specialist: "", commission_tier: "25", expiry_date: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        name: editing.name,
        username: editing.username,
        email: editing.email,
        specialist: editing.specialist,
        commission_tier: editing.commission_tier,
        join_date: editing.join_date,
        expiry_date: editing.expiry_date,
        member_id: editing.member_id,
        active: editing.active,
      };
      if (editing.new_password) payload.new_password = editing.new_password;
      if (editing.photo_base64) payload.photo_base64 = editing.photo_base64;
      // Strip empty strings so backend keeps existing values
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      });
      await axios.patch(`${API}/team/members/${editing.id}`, payload, { headers: authHeader() });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const handleDeactivate = async (m) => {
    if (!window.confirm(`Deactivate ${m.name}?`)) return;
    try {
      await axios.delete(`${API}/team/members/${m.id}`, { headers: authHeader() });
      toast.success("Deactivated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadCard = async (m) => {
    try {
      const res = await fetch(`${API}/team/members/${m.id}/id-card`, { headers: authHeader() });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `MK_ID_${m.member_id || m.id}.pdf`; a.click();
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="team-tab">
      <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <h3 className="font-medium text-lg flex items-center gap-2">
            <Users size={18} className="text-[#B8860B]" strokeWidth={1.5} /> Team
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Founders, Head of Operations, and Talent Partners. Create, manage, and download ID cards.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-[#B8860B] text-white px-4 py-2 text-sm hover:bg-[#9A7209]"
          data-testid="add-team-btn">
          <UserPlus size={14} /> Add Team Member
        </button>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="text-center py-16 text-gray-400"><Loader2 className="animate-spin inline" size={16} /></div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No team members yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Specialist</th>
                <th className="text-left p-3">Member ID</th>
                <th className="text-left p-3">Tier</th>
                <th className="text-left p-3">Earnings</th>
                <th className="text-left p-3">Expires</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className={`border-b border-gray-50 ${m.active === false ? "opacity-40" : ""}`} data-testid={`member-row-${m.id}`}>
                  <td className="p-3">
                    <p className="font-medium text-gray-800">{m.name}</p>
                    <p className="text-xs text-gray-400">{m.email}</p>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-xs border rounded-full ${ROLE_BADGE[m.role]}`}>
                      {ROLE_LABEL[m.role] || m.role}
                    </span>
                  </td>
                  <td className="p-3 text-gray-700">{m.specialist || "-"}</td>
                  <td className="p-3 text-gray-600 font-mono text-xs">{m.member_id || "-"}</td>
                  <td className="p-3">{m.commission_tier ? `${m.commission_tier}%` : "-"}</td>
                  <td className="p-3">{m.earnings ? `£${m.earnings.total_earnings.toLocaleString()}` : "-"}</td>
                  <td className="p-3 text-xs text-gray-500">{m.expiry_date || "Permanent"}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => downloadCard(m)} title="Download ID Card" className="p-1.5 hover:bg-gray-100 rounded" data-testid={`download-idcard-${m.id}`}>
                        <CreditCard size={14} className="text-[#B8860B]" />
                      </button>
                      {(m.role !== "super_admin" || isSuperAdmin) && (
                        <button onClick={() => setEditing(m)} title="Edit" className="p-1.5 hover:bg-gray-100 rounded" data-testid={`edit-${m.id}`}>
                          <Edit2 size={14} className="text-gray-500" />
                        </button>
                      )}
                      {m.role !== "super_admin" && m.active !== false && (
                        <button onClick={() => handleDeactivate(m)} title="Deactivate" className="p-1.5 hover:bg-gray-100 rounded" data-testid={`deactivate-${m.id}`}>
                          <Trash2 size={14} className="text-red-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Add Team Member" testId="add-team-modal">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Full Name *" v={form.name} s={(v) => setForm({ ...form, name: v })} t="new-name" />
            <Input label="Email *" v={form.email} s={(v) => setForm({ ...form, email: v })} t="new-email" type="email" />
            <Input label="Username *" v={form.username} s={(v) => setForm({ ...form, username: v })} t="new-username" />
            <Input label="Password * (8+ chars)" v={form.password} s={(v) => setForm({ ...form, password: v })} t="new-password" type="password" />
            <Select label="Role" v={form.role} s={(v) => setForm({ ...form, role: v })} t="new-role"
              opts={[{ v: "contractor", l: "Talent Partner" }, ...(isSuperAdmin ? [{ v: "admin", l: "Head of Operations" }] : [])]} />
            <Select label="Specialist" v={form.specialist}
              s={(v) => setForm({
                ...form,
                specialist: v,
                // Auto-suggest tier based on specialist track; admin can still override.
                commission_tier: v === "Business Development" ? "10" : (form.commission_tier === "10" ? "25" : form.commission_tier || "25"),
              })}
              t="new-specialist"
              opts={[{ v: "", l: "-- Select --" }, ...options.specialists.map((s) => ({ v: s, l: s }))]} />
            {form.role === "contractor" && (
              <Select label="Commission Tier" v={form.commission_tier} s={(v) => setForm({ ...form, commission_tier: v })} t="new-tier"
                opts={Object.entries(options.commission_tiers).map(([v, l]) => ({ v, l }))} />
            )}
            <Input label="Custom Expiry (optional)" v={form.expiry_date} s={(v) => setForm({ ...form, expiry_date: v })} t="new-expiry" type="date" />
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50">Cancel</button>
            <button onClick={handleCreate} className="bg-[#B8860B] text-white px-4 py-2 text-sm hover:bg-[#9A7209]" data-testid="confirm-create">Create</button>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Edit ${editing.name}`} testId="edit-team-modal">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Full Name" v={editing.name || ""} s={(v) => setEditing({ ...editing, name: v })} t="edit-name" />
            <Input label="Username (login)" v={editing.username || ""} s={(v) => setEditing({ ...editing, username: v })} t="edit-username" />
            <Input label="Email" v={editing.email || ""} s={(v) => setEditing({ ...editing, email: v })} t="edit-email" type="email" />
            <Select label="Specialist" v={editing.specialist || ""} s={(v) => setEditing({ ...editing, specialist: v })} t="edit-specialist"
              opts={[{ v: "", l: "-- Select --" }, ...options.specialists.map((s) => ({ v: s, l: s }))]} />
            {editing.role === "contractor" && (
              <Select label="Commission Tier" v={editing.commission_tier || "25"} s={(v) => setEditing({ ...editing, commission_tier: v })} t="edit-tier"
                opts={Object.entries(options.commission_tiers).map(([v, l]) => ({ v, l }))} />
            )}
            <Input label="Join Date" v={editing.join_date || ""} s={(v) => setEditing({ ...editing, join_date: v })} t="edit-join-date" type="date" />
            <Input label="Expiry Date" v={editing.expiry_date || ""} s={(v) => setEditing({ ...editing, expiry_date: v })} t="edit-expiry" type="date" />
            <Input label="Member ID" v={editing.member_id || ""} s={(v) => setEditing({ ...editing, member_id: v })} t="edit-member-id" />
            <Input label="Reset Password (8+ chars, leave blank to keep)" v={editing.new_password || ""} s={(v) => setEditing({ ...editing, new_password: v })} t="edit-password" type="password" />
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 uppercase">Profile Photo (max ~350KB)</label>
              <input type="file" accept="image/*" data-testid="edit-photo"
                onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 350*1024) { toast.error("Photo too large (max 350KB)"); return; }
                  const reader = new FileReader();
                  reader.onload = (ev) => setEditing({ ...editing, photo_base64: ev.target.result });
                  reader.readAsDataURL(f);
                }}
                className="w-full border border-gray-200 px-3 py-2 text-sm" />
              {editing.photo_base64 && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={editing.photo_base64} alt="" className="w-16 h-20 object-cover border border-gray-200 rounded" />
                  <button onClick={() => setEditing({ ...editing, photo_base64: "" })} className="text-xs text-red-600 hover:underline">Remove photo</button>
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="edit-active" />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50">Cancel</button>
            <button onClick={handleUpdate} className="bg-[#B8860B] text-white px-4 py-2 text-sm hover:bg-[#9A7209]" data-testid="confirm-edit">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ onClose, title, children, testId }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white max-w-xl w-full max-h-[90vh] overflow-y-auto rounded shadow-xl" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex justify-between items-center">
          <h3 className="text-lg font-medium">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Input({ label, v, s, t, type = "text" }) {
  return (
    <div>
      <label className="text-xs text-gray-500 uppercase">{label}</label>
      <input type={type} value={v} onChange={(e) => s(e.target.value)}
        className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#B8860B]" data-testid={t} />
    </div>
  );
}

function Select({ label, v, s, t, opts }) {
  return (
    <div>
      <label className="text-xs text-gray-500 uppercase">{label}</label>
      <select value={v} onChange={(e) => s(e.target.value)}
        className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#B8860B]" data-testid={t}>
        {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

export default TeamTab;
